import { formatSupplementalNameEditPayload } from "@/services/helpers";
import { getSupplementalsByEntity } from "@/services/supplementals";

import type { DialogTabSaveContext } from "@/v2/framework/dialog/types";

export const SUPPLEMENTAL_NAME_EDITS_KEY = "_supplementalNameEdits";

export type SupplementalNameEditRow = {
  supplementalNameTypeId: string | number;
  computedName: string | null;
  parentName?: string | null;
  sourcePolicy?: number;
};

export function buildAddSupplementalNamesPayload(
  values: Record<string, unknown>,
  entity: string,
): { supplementalNameTypeId: string | number; name: string | null }[] {
  const supplementalNameTypes = getSupplementalsByEntity(entity);

  return supplementalNameTypes.map((type) => {
    const raw = values[`supplemental_${type.id}`];
    return {
      supplementalNameTypeId: type.id,
      name: raw == null || raw === "" ? null : String(raw),
    };
  });
}

export function buildSupplementalNamesPatch(
  values: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> | null {
  const edits = values[SUPPLEMENTAL_NAME_EDITS_KEY];
  if (!edits || typeof edits !== "object" || Array.isArray(edits)) return null;

  const editRows = Object.values(edits as Record<string, SupplementalNameEditRow>);
  if (!editRows.length) return null;

  const baselineEdits = baseline[SUPPLEMENTAL_NAME_EDITS_KEY];
  const baselineMap =
    baselineEdits && typeof baselineEdits === "object" && !Array.isArray(baselineEdits)
      ? (baselineEdits as Record<string, SupplementalNameEditRow>)
      : {};

  let supplementalNames: Record<string, unknown>[] | null = null;
  const supplementalPolicies: Record<string, { name: number }> = {};
  let hasChanges = false;

  for (const edit of editRows) {
    const key = String(edit.supplementalNameTypeId);
    const baselineRow = baselineMap[key];
    const baselineName = baselineRow?.computedName ?? null;
    const nextName = edit.computedName ?? null;

    if (baselineName !== nextName) {
      supplementalNames = formatSupplementalNameEditPayload({
        supplementalNameList: supplementalNames,
        supplementalNameTypeId: edit.supplementalNameTypeId,
        value: nextName,
        key: "name",
      }) as Record<string, unknown>[];
      hasChanges = true;
    }

    const baselinePolicy = baselineRow?.sourcePolicy ?? 1;
    const nextPolicy = edit.sourcePolicy ?? 1;
    if (baselinePolicy !== nextPolicy) {
      supplementalPolicies[key] = { name: nextPolicy };
      hasChanges = true;
    }
  }

  if (!hasChanges) return null;

  const patch: Record<string, unknown> = {};
  if (supplementalNames) patch.supplementalNames = supplementalNames;
  if (Object.keys(supplementalPolicies).length) {
    patch.sourcePolicyMap = { supplementalNames: supplementalPolicies };
  }

  return patch;
}

export function syncSupplementalNamesBaseline(ctx: DialogTabSaveContext): void {
  const edits = ctx.values[SUPPLEMENTAL_NAME_EDITS_KEY];
  if (edits && typeof edits === "object" && !Array.isArray(edits)) {
    ctx.baseline[SUPPLEMENTAL_NAME_EDITS_KEY] = JSON.parse(JSON.stringify(edits));
  }
}
